import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, SxProps, Theme, Tooltip, Typography } from '@mui/material';

interface DualListItem {
    name: string;
    label: string;
}

interface DualListPanelProps {
    title: string;
    color: string;
    items: DualListItem[];
    emptyLabel: string;
    boxSx: SxProps<Theme>;
    onRemove?: (name: string) => void;
}

// Shows the label on one line, ellipsis-truncated if it doesn't fit — and only then wraps it in a
// Tooltip, so hovering a short label that already fits doesn't pop up a redundant tooltip.
function TruncatedItemLabel({ label }: { label: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    const content = (
        <Typography
            ref={ref}
            variant="body2"
            sx={{ color: '#495057', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
            {label}
        </Typography>
    );

    return isTruncated ? (
        <Tooltip title={label} arrow>
            {content}
        </Tooltip>
    ) : content;
}

function DualListPanel({ title, color, items, emptyLabel, boxSx, onRemove }: DualListPanelProps) {
    return (
        // minWidth: 0 lets this panel actually shrink to its equal flex share instead of growing
        // past it to fit an unbroken long item label (the cause of the two side-by-side panels
        // ending up visibly different widths) — required for text-overflow: ellipsis to kick in too.
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color }}>
                {title} {onRemove && `(${items.length})`}
            </Typography>
            <Box
                sx={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    px: 1,
                    py: 0.5,
                    ...boxSx,
                }}
            >
                {items.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        {emptyLabel}
                    </Typography>
                ) : onRemove ? (
                    items.map(item => (
                        <Chip
                            key={item.name}
                            size="small"
                            sx={{ m: 0.25, maxWidth: '100%' }}
                            label={item.label}
                            onDelete={() => onRemove(item.name)}
                        />
                    ))
                ) : (
                    items.map(item => (
                        <TruncatedItemLabel key={item.name} label={item.label} />
                    ))
                )}
            </Box>
        </Box>
    );
}

export default DualListPanel;
